import { createContext, useContext, useState, useCallback, useMemo } from "react";
import en from "./en.json";
import ptBR from "./pt-BR.json";

const translations = { en, "pt-BR": ptBR };
const SUPPORTED_LOCALES = ["en", "pt-BR"];
const STORAGE_KEY = "viewsday_lang";

function detectLocale() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;

  const nav = navigator.language || navigator.languages?.[0] || "en";
  if (nav.startsWith("pt")) return "pt-BR";
  return "en";
}

function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(detectLocale);

  const setLocale = useCallback((newLocale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      localStorage.setItem(STORAGE_KEY, newLocale);
      setLocaleState(newLocale);
      document.documentElement.lang = newLocale;
    }
  }, []);

  const t = useCallback(
    (key, params) => {
      let value = getNestedValue(translations[locale], key)
        ?? getNestedValue(translations.en, key)
        ?? key;

      if (params && typeof value === "string") {
        Object.entries(params).forEach(([k, v]) => {
          value = value.replace(new RegExp(`\\{${k}\\}`, "g"), v);
        });
      }
      return value;
    },
    [locale]
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, locales: SUPPORTED_LOCALES }),
    [locale, setLocale, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useTranslation must be used within LanguageProvider");
  return ctx;
}
